import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { User } from '../../../types/user.types';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';

export interface UserDeleteModalProps {
  open: boolean;
  users: User[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
}

const UserDeleteModal: React.FC<UserDeleteModalProps> = ({
  open,
  users,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error deleting users:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!users || users.length === 0) {
    return null;
  }

  const usersWithRoles = users.filter((user) => user.roles?.length > 0);
  const totalRoles = usersWithRoles.reduce((sum, user) => sum + (user.roles?.length || 0), 0);

  const customContent =
    users.length === 1 ? (
      <Box
        sx={{
          p: 2.5,
          borderRadius: '8px',
          backgroundColor: colors.grey[50],
          border: `1px solid ${colors.grey[200]}`,
          textAlign: 'left',
          mt: 2,
        }}
      >
        {usersWithRoles.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este usuario tiene {users[0].roles.length} rol(es) asignado(s). Se quitarán sus
            asignaciones.
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          Se eliminará de la gestión de usuarios. El historial se conserva.
        </Alert>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'block',
            mb: 1,
          }}
        >
          Usuario
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            mb: 1,
          }}
        >
          {users[0].email}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
          {`${users[0].firstName} ${users[0].lastName}`.trim()}
        </Typography>
      </Box>
    ) : (
      <Box
        sx={{
          mt: 2,
          borderRadius: '8px',
          border: `1px solid ${colors.grey[200]}`,
          overflow: 'hidden',
        }}
      >
        {usersWithRoles.length > 0 && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            Se quitarán las asignaciones de {totalRoles} rol(es).
          </Alert>
        )}
        <Alert severity="info" sx={{ m: 2, mb: 0 }}>
          Se eliminarán de la gestión de usuarios. El historial se conserva.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.grey[50] }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Cuenta</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Roles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell sx={{ fontSize: '0.875rem' }}>{user.email}</TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  {`${user.firstName} ${user.lastName}`.trim() || '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>{user.roles?.length || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const title =
    users.length === 1
      ? `Eliminar Usuario ${users[0]?.email || ''}`
      : `Eliminar ${users.length} Usuarios`;

  const message =
    users.length === 1
      ? `¿Desea eliminar el usuario "${users[0]?.email || ''}" de la gestión? El historial se conserva.`
      : `¿Desea eliminar estos ${users.length} usuarios de la gestión? El historial se conserva.`;

  const checkboxLabel =
    users.length === 1
      ? 'Confirmo eliminar este usuario de la gestión'
      : `Confirmo eliminar estos ${users.length} usuarios de la gestión`;

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={title}
      message={message}
      confirmText={isDeleting ? 'Eliminando...' : 'Eliminar'}
      cancelText="Cancelar"
      variant="danger"
      icon={<DeleteIcon />}
      loading={isDeleting || loading}
      requireCheckbox
      checkboxLabel={checkboxLabel}
      customContent={customContent}
    />
  );
};

export default UserDeleteModal;
